import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import { LeadFormActions } from '../../Services/_actions/index.js';
import { ImageModal } from '../Modals/ImageModal/index.jsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../Whatsapp/Template/template.constant.js';

interface ViewImagesModalProps {
  open: boolean;
  onClose: () => void;
  selectedRow?: any;
  onSaveImage?: (
    image: { url: string; file_name: string; updated_by: string; updated_at: string },
    id: string
  ) => void;
}

const ViewImagesModal: React.FC<ViewImagesModalProps> = ({
  open,
  onClose,
  selectedRow,
  onSaveImage,
}) => {
  const actions = LeadFormActions();
  const [imageList, setImageList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const { showSnackBar } = useSnackBar();

  useEffect(() => {
    if (selectedRow?.image_funnel) setImageList([...selectedRow.image_funnel]);
  }, [selectedRow]);

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fileName = file?.name?.toLowerCase();
    const ext = fileName.split('.').pop();
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      event.target.value = '';
      return;
    }
    const typeGroup = inferDocType(file.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
      event.target.value = '';
      return;
    }

    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file?.size)} MB.`,
        'error'
      );
      event.target.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response: any = await actions.uploadLeadImageById(selectedRow?.id, formData);

      if (response?.status === 200) {
        const localUrl = URL.createObjectURL(file);
        const newImage = {
          url: localUrl,
          file_name: file?.name || '',
          updated_by: 'You', // You can change this to actual user name if available
          updated_at: new Date().toISOString(), // Convert Date to string (ISO format)
        };
        // Save image to parent component
        if (onSaveImage) {
          onSaveImage(newImage, selectedRow.id); // Pass selectedRow.id to associate image with the right row
        }
        setImageList((prev) => [...prev, newImage]);
        showSnackBar('Image uploaded successfully!', 'success');
      } else {
        showSnackBar('Failed to upload image', 'error');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showSnackBar('Upload error occurred', 'error');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleCloseModal = () => {
    if (imageList.length > 0 && onSaveImage) {
      imageList.forEach((image) => {
        onSaveImage(image, selectedRow.id); // Save all images to parent
      });
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          width:{xs: 'calc(100% - 40px)', sm: 460},
          maxHeight: '80vh',
          overflowY: 'hidden',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            View & Upload Images
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Upload Section */}
        <Box mb={2}>
          <input
            accept="image/*"
            type="file"
            id="upload-image"
            multiple
            hidden
            onChange={handleImageUpload}
          />
          <FormHelperText sx={{ mt: 1 }}>Allowed: Images</FormHelperText>
          <label htmlFor="upload-image">
            <Button variant="contained" component="span" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Images'}
            </Button>
          </label>
        </Box>

        <Divider sx={{ mt: 2 }} />

        {/* Image List */}
        <ImageModal imagesList={imageList} />
      </Box>
    </Modal>
  );
};

export default ViewImagesModal;
